// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useMatch, useLocation } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import type { Account, AccountType } from '../../types.js';
import { formatAmount } from '../../lib/eventLog.js';
import { generateGnuCashXml } from '../../lib/gnucashXml.js';
import { CloseIcon } from '../mobile/icons.js';

// The global-search shortcut hint: ⌘ only means anything on macOS, so show the
// spelled-out Ctrl on Windows/Linux. navigator.platform is deprecated but still
// the most reliable synchronous signal; fall back to the userAgent string.
const IS_MAC =
  typeof navigator !== 'undefined' && /mac/i.test(navigator.platform || navigator.userAgent || '');
const SEARCH_HINT = IS_MAC ? '⌘K' : 'Ctrl K';

const ACCOUNT_GROUPS: { label: string; types: AccountType[] }[] = [
  { label: 'Assets', types: ['ASSET', 'BANK', 'CASH', 'INVESTMENT', 'RECEIVABLE', 'MUTUAL_FUND', 'STOCK', 'ACCOUNTS_RECEIVABLE'] },
  { label: 'Liabilities', types: ['LIABILITY', 'CREDIT', 'CREDIT_CARD', 'PAYABLE', 'ACCOUNTS_PAYABLE'] },
  { label: 'Income', types: ['INCOME'] },
  { label: 'Expenses', types: ['EXPENSE'] },
  { label: 'Equity', types: ['EQUITY'] },
  { label: 'Trading', types: ['TRADING'] },
];

interface AccountNodeProps {
  account: Account;
  allAccounts: Account[];
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function AccountNode({ account, allAccounts, depth, selectedId, onSelect }: AccountNodeProps) {
  const [expanded, setExpanded] = useState(true);
  const children = allAccounts.filter((a) => a.parentId === account.id && !a.hidden);
  const isSelected = selectedId === account.id;

  return (
    <div>
      <button
        onClick={() => {
          if (!account.placeholder) onSelect(account.id);
          if (children.length > 0) setExpanded(!expanded);
        }}
        className={`w-full flex items-center gap-1 text-sm text-left transition-colors relative ${
          isSelected
            ? 'bg-bg-elevated text-text border-l-2 border-accent'
            : 'text-text-muted hover:text-text hover:bg-bg-elevated border-l-2 border-transparent'
        }`}
        style={{ paddingLeft: `${10 + depth * 12}px`, paddingTop: '4px', paddingBottom: '4px', paddingRight: '8px' }}
      >
        {children.length > 0 ? (
          <span className="text-text-subtle text-xs w-3 shrink-0">{expanded ? '▾' : '▸'}</span>
        ) : (
          <span className="w-3 shrink-0" />
        )}
        <span className="flex-1 truncate">{account.name}</span>
        {!account.placeholder && (
          <span className="text-xs text-text-subtle shrink-0 ml-1 font-mono">
            {formatAmount(account.balance, account.currency)}
          </span>
        )}
      </button>
      {expanded &&
        children.map((child) => (
          <AccountNode
            key={child.id}
            account={child}
            allAccounts={allAccounts}
            depth={depth + 1}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

interface NavItemProps {
  to: string;
  label: string;
  hint?: string;
}

function NavItem({ to, label, hint }: NavItemProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <button
      onClick={() => navigate(to)}
      className={`w-full flex items-center justify-between px-3 py-1 text-sm text-left transition-colors relative border-l-2 ${
        isActive
          ? 'bg-bg-elevated text-text border-accent'
          : 'text-text-muted hover:text-text hover:bg-bg-elevated border-transparent'
      }`}
    >
      <span>{label}</span>
      {hint && <span className="text-text-subtle text-xs">{hint}</span>}
    </button>
  );
}

interface ReportsNavProps {
  items: { to: string; label: string }[];
}

function ReportsNav({ items }: ReportsNavProps) {
  const { t } = useTranslation(['common', 'mobile', 'errors']);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(location.pathname.startsWith('/reports'));
  const isActive = location.pathname.startsWith('/reports');

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-3 py-1 text-sm text-left transition-colors relative border-l-2 ${
          isActive
            ? 'bg-bg-elevated text-text border-accent'
            : 'text-text-muted hover:text-text hover:bg-bg-elevated border-transparent'
        }`}
      >
        <span>{t('nav.reports')}</span>
        <span className="text-text-subtle text-xs">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div>
          {items.map(({ to, label }) => {
            const active = location.pathname === to;
            return (
              <button
                key={to}
                onClick={() => navigate(to)}
                className={`w-full flex items-center px-3 py-0.5 text-sm text-left transition-colors border-l-2 ${
                  active
                    ? 'bg-bg-elevated text-text border-accent'
                    : 'text-text-muted hover:text-text hover:bg-bg-elevated border-transparent'
                }`}
                style={{ paddingLeft: '24px' }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface SidebarProps {
  // Below md the sidebar is an off-canvas drawer; `open` slides it in and
  // `onClose` is fired by the drawer's close button. At md+ it is always visible
  // and these are no-ops.
  open?: boolean;
  onClose?: () => void;
}

export function Sidebar({ open = false, onClose }: SidebarProps) {
  const { t } = useTranslation(['common', 'mobile', 'errors', 'accounting', 'reports']);
  const { state } = useApp();
  const { ledger, sync, auth } = state;
  const navigate = useNavigate();

  const groupLabel: Record<string, string> = {
    Assets: t('accounting:accountTypeGroups.assets'),
    Liabilities: t('accounting:accountTypeGroups.liabilities'),
    Income: t('accounting:accountTypeGroups.income'),
    Expenses: t('accounting:accountTypeGroups.expenses'),
    Equity: t('accounting:accountTypeGroups.equity'),
    Trading: t('accounting:accountTypeGroups.trading'),
  };

  const match = useMatch('/accounts/:id');
  const selectedId = match?.params.id ?? null;

  const accounts = [...ledger.accounts.values()].filter((a) => !a.hidden);

  const syncDot =
    sync.status === 'syncing' ? '○' : sync.status === 'error' ? '×' : '●';
  const syncDotColor =
    sync.status === 'syncing'
      ? 'text-text-subtle'
      : sync.status === 'error'
      ? 'text-negative'
      : 'text-positive';

  function syncLabel() {
    if (sync.status === 'syncing') return t('sync.syncing');
    if (sync.status === 'error') return t('sync.error');
    if (sync.lastSyncAt) {
      const diff = Math.floor((Date.now() - new Date(sync.lastSyncAt).getTime()) / 60000);
      if (diff < 1) return t('sync.syncedJustNow');
      if (diff < 60) return t('sync.syncedMinutesAgo', { count: diff });
      return t('sync.syncedHoursAgo', { count: Math.floor(diff / 60) });
    }
    return t('sync.notSynced');
  }

  const isAdmin = auth.isAuthenticated && typeof window !== 'undefined' && window.location.hostname === 'localhost';

  // Download the full ledger as a GnuCash XML file. Mirrors the export that used
  // to live in the top-right user menu; the in-memory event log is enough here.
  function handleExportGnuCash() {
    try {
      const xml = generateGnuCashXml(state.events);
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hushold-${new Date().toISOString().slice(0, 10)}.gnucash`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('mobile:errors.exportFailed', { message: err instanceof Error ? err.message : String(err) }));
    }
  }

  return (
    <aside
      className={`fixed top-12 bottom-0 left-0 z-40 w-[220px] border-r border-border bg-bg-surface flex flex-col text-sm no-print transition-transform duration-200 md:static md:top-auto md:bottom-auto md:z-auto md:translate-x-0 md:shrink-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      {/* Mobile-only drawer header (desktop shows no header — the TopNav logo covers that role) */}
      <div className="px-3 py-3 border-b border-border shrink-0 flex items-center justify-end md:hidden">
        <button
          onClick={onClose}
          aria-label={t('a11y.closeNavigation')}
          className="p-1 text-text-muted hover:text-text transition-colors"
        >
          <CloseIcon size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ACCOUNTS section */}
        <div className="pt-3 pb-1">
          <p className="px-3 pb-1 text-xs text-text-subtle uppercase tracking-widest font-medium">
            {t('nav.accounts')}
          </p>
          <NavItem to="/accounts" label={t('nav.chartOfAccounts')} />
          {accounts.length > 0 &&
            ACCOUNT_GROUPS.map(({ label, types }) => {
              const groupAccounts = accounts.filter(
                (a) => (types as string[]).includes(a.type) && a.parentId === null,
              );
              if (groupAccounts.length === 0) return null;
              return (
                <div key={label} className="mt-0.5">
                  <p className="px-3 pt-1 text-xs text-text-subtle font-medium">{groupLabel[label]}</p>
                  {groupAccounts.map((account) => (
                    <AccountNode
                      key={account.id}
                      account={account}
                      allAccounts={accounts}
                      depth={0}
                      selectedId={selectedId}
                      onSelect={(id) => navigate(`/accounts/${id}`)}
                    />
                  ))}
                </div>
              );
            })}
        </div>

        {/* TOOLS section */}
        <div className="pt-3 pb-1 border-t border-border mt-1">
          <p className="px-3 pb-1 text-xs text-text-subtle uppercase tracking-widest font-medium">
            {t('nav.tools')}
          </p>
          <NavItem to="/portfolio" label={t('nav.portfolio')} />
          <ReportsNav
            items={[
              { to: '/reports/transactions', label: t('nav.transactions') },
              { to: '/reports/balance-sheet', label: t('reports:titles.balanceSheet') },
              { to: '/reports/income-statement', label: t('reports:titles.incomeStatement') },
              { to: '/reports/cash-flow', label: t('reports:titles.cashFlow') },
              { to: '/reports/net-worth', label: t('reports:titles.netWorth') },
              { to: '/reports/expense-breakdown', label: t('reports:titles.expenseBreakdown') },
              { to: '/reports/income-vs-expenses', label: t('reports:titles.incomeVsExpenses') },
              { to: '/reports/budget', label: t('reports:titles.budgetReport') },
              { to: '/reports/capital-gains', label: t('nav.capitalGains') },
              { to: '/reports/currency-gain-loss', label: t('reports:titles.currencyGainLoss') },
            ]}
          />
          <NavItem to="/budgets" label={t('nav.budgets')} />
          <NavItem to="/scheduled-transactions" label={t('nav.scheduled')} />
          <NavItem to="/settings/commodities" label={t('nav.securities')} />
          <NavItem to="/settings/exchange-rates" label={t('nav.exchangeRates')} />
          <NavItem to="/search" label={t('nav.searchTransactions')} hint={SEARCH_HINT} />
        </div>

        {/* ADMIN section — only shown when relevant */}
        {isAdmin && (
          <div className="pt-3 pb-1 border-t border-border mt-1">
            <p className="px-3 pb-1 text-xs text-text-subtle uppercase tracking-widest font-medium">
              {t('nav.admin')}
            </p>
            <NavItem to="/admin" label={t('nav.users')} />
          </div>
        )}
      </div>

      {/* Actions pinned to the bottom of the bar (above the sync status). */}
      <div className="shrink-0 border-t border-border py-1">
        <NavItem to="/import" label={t('nav.import')} />
        <button
          onClick={handleExportGnuCash}
          className="w-full flex items-center px-3 py-1 text-sm text-left transition-colors border-l-2 border-transparent text-text-muted hover:text-text hover:bg-bg-elevated"
        >
          {t('nav.export')}
        </button>
        <NavItem to="/settings" label={t('nav.settings')} />
      </div>

      {/* Sync status pinned to bottom */}
      <div className="px-3 py-2 border-t border-border shrink-0">
        <span className={`text-xs ${syncDotColor}`}>{syncDot}</span>
        <span className="text-xs text-text-subtle ml-1.5">{syncLabel()}</span>
      </div>
    </aside>
  );
}
